// Ported from design_ideas/lernwelt/bundle.jsx — submit / next + feedback row.
// The unsubmitted state also hosts the 💡 Tipp button that explicitly reveals
// the next hint tier (replacing the old timer-based auto-reveal).

interface Props {
  canSubmit: boolean;
  submitted: boolean;
  isCorrect: boolean | null;
  onSubmit: () => void;
  onNext: () => void;
  explanation: string;
  triggerExplain?: boolean;
  // Hint controls — only rendered pre-submit when hints exist.
  hintsTotal?: number;
  hintTier?: number;              // -1 = none shown yet
  onRequestHint?: () => void;
  canRequestHint?: boolean;       // gated on wait-time + narrativeDone + tiers remaining
}

export default function ActionRow({
  canSubmit, submitted, isCorrect, onSubmit, onNext, explanation, triggerExplain = false,
  hintsTotal = 0, hintTier = -1, onRequestHint, canRequestHint = false,
}: Props) {
  if (!submitted) {
    const hintsRemaining = Math.max(0, hintsTotal - (hintTier + 1));
    const showHintBtn = hintsTotal > 0 && !!onRequestHint;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: '0.18em',
          color: 'var(--ink-muted)',
          textTransform: 'uppercase',
        }}>
          {showHintBtn && (
            <button
              onClick={canRequestHint ? onRequestHint : undefined}
              disabled={!canRequestHint}
              aria-label={hintsRemaining > 0 ? `Tipp anfordern (noch ${hintsRemaining})` : 'Keine Tipps mehr übrig'}
              style={{
                minWidth: 44, minHeight: 44,
                padding: '10px 16px',
                background: canRequestHint ? 'var(--amber-soft)' : 'rgba(60,50,40,0.06)',
                color: canRequestHint ? 'var(--amber-deep)' : 'var(--ink-muted)',
                border: `1.5px solid ${canRequestHint ? 'var(--amber)' : 'rgba(60,50,40,0.15)'}`,
                borderRadius: 999,
                fontFamily: 'var(--sans)',
                fontSize: 13, fontWeight: 700,
                letterSpacing: '0.04em',
                cursor: canRequestHint ? 'pointer' : 'default',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 16 }}>💡</span>
              <span>{hintsRemaining > 0 ? 'Tipp' : 'Keine Tipps mehr'}</span>
              {hintsTotal > 1 && hintsRemaining > 0 && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, opacity: 0.7 }}>
                  {hintTier + 2}/{hintsTotal}
                </span>
              )}
            </button>
          )}
          <span>{canSubmit ? 'Bereit? Absenden →' : 'Wähle eine Antwort'}</span>
        </div>
        <button
          onClick={canSubmit ? onSubmit : undefined}
          disabled={!canSubmit}
          style={{
            padding: '14px 28px',
            background: canSubmit ? 'var(--amber)' : 'rgba(60,50,40,0.1)',
            color: canSubmit ? '#fff' : 'var(--ink-muted)',
            border: 'none',
            borderRadius: 3,
            fontFamily: 'var(--sans)',
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '0.02em',
            cursor: canSubmit ? 'pointer' : 'default',
            transition: 'all 0.15s',
            boxShadow: canSubmit ? '0 4px 12px rgba(201,106,47,0.35)' : 'none',
          }}
          onMouseEnter={e => { if (canSubmit) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
        >Absenden</button>
      </div>
    );
  }

  // Still waiting for API response
  if (isCorrect === null) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          background: 'rgba(60,50,40,0.05)',
          border: '1px solid rgba(60,50,40,0.12)',
          borderRadius: 3,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: '0.18em',
          color: 'var(--ink-muted)',
          textTransform: 'uppercase',
          animation: 'cursor-blink 1s steps(2) infinite',
        }}>Finn überlegt…</div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        background: isCorrect ? 'var(--correct-soft)' : 'var(--wrong-soft)',
        border: `1px solid ${isCorrect ? 'var(--correct)' : 'var(--wrong)'}`,
        borderRadius: 3,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        animation: 'fade-in-up 0.3s ease-out',
      }}
    >
      <div style={{
        fontFamily: 'var(--display)',
        fontSize: 24,
        fontWeight: 600,
        color: isCorrect ? 'var(--correct)' : 'var(--wrong)',
        lineHeight: 1,
        minWidth: 100,
      }}>
        {isCorrect ? 'Stark!' : 'Fast!'}
      </div>
      <div style={{
        flex: 1,
        fontFamily: 'var(--serif)',
        fontSize: 14,
        fontStyle: 'italic',
        color: 'var(--ink)',
        lineHeight: 1.4,
      }}>{explanation}</div>
      <button
        onClick={onNext}
        style={{
          padding: '10px 20px',
          background: triggerExplain ? 'var(--amber)' : 'var(--ink)',
          color: 'var(--parchment)',
          border: 'none',
          borderRadius: 3,
          fontFamily: 'var(--sans)',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.04em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          boxShadow: triggerExplain ? '0 4px 12px rgba(201,106,47,0.35)' : 'none',
        }}
      >{triggerExplain ? 'Finn erklärt →' : 'Weiter →'}</button>
    </div>
  );
}
